import { useEffect, useRef, useState } from "react";
import { ImagePlus, Images, RefreshCw, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { MediaPickerDialog } from "../../components/shared/MediaPickerDialog";
import { EmptyState } from "../../components/shared/EmptyState";
import { api, formatApiError } from "../../lib/api";

const PAGE_ORDER = ["accueil", "boutique", "coordination", "mon-histoire", "realisations", "blog", "partenaires", "contact", "rendez-vous"];

export default function PagesSitePage() {
  const [slots, setSlots] = useState(null);
  const [texts, setTexts] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [alts, setAlts] = useState({});
  const [pickerSlot, setPickerSlot] = useState(null);
  const [busy, setBusy] = useState(null);
  const fileInputs = useRef({});

  const load = () => {
    api.get("/admin/page-slots").then((r) => setSlots(r.data)).catch(() => toast.error("Emplacements indisponibles"));
    api.get("/admin/page-texts").then((r) => {
      setTexts(r.data);
      setDrafts(Object.fromEntries(r.data.map((t) => [t.key, t.value])));
    }).catch(() => toast.error("Textes indisponibles"));
  };
  useEffect(() => { load(); }, []);

  const bind = async (slot, mediaId) => {
    try {
      await api.put(`/admin/page-slots/${encodeURIComponent(slot)}`, { media_id: mediaId });
      toast.success("Image associée — visible immédiatement sur le site");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const unbind = async (slot) => {
    try {
      await api.delete(`/admin/page-slots/${encodeURIComponent(slot)}`);
      toast.success("Image retirée — la page repasse au visuel par défaut");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const upload = async (slot, file) => {
    if (!file) return;
    setBusy(slot);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("name", file.name.replace(/\.[^.]+$/, ""));
      form.append("folder", "Pages du site");
      const res = await api.post("/admin/media/upload", form);
      await bind(slot, res.data.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const saveAlt = async (mediaId) => {
    try {
      await api.patch(`/admin/media/${mediaId}`, { alt: alts[mediaId] ?? "" });
      toast.success("Texte alternatif enregistré");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveText = async (key) => {
    try {
      await api.put(`/admin/page-texts/${encodeURIComponent(key)}`, { value: drafts[key] ?? "" });
      toast.success("Texte enregistré — visible immédiatement sur le site");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!slots || !texts) return <div className="py-16 text-center font-heading text-deepgreen" data-testid="pages-loading">Chargement…</div>;

  const pages = PAGE_ORDER.map((p) => ({ page: p, slots: slots.filter((s) => s.slot.split(":")[0] === p) }))
    .filter((g) => g.slots.length > 0);

  return (
    <div data-testid="admin-pages-page">
      <p className="overline-ae">A01-A25 — Pages du site</p>
      <h1 className="mt-1 font-heading text-2xl sm:text-3xl text-deepgreen">Contenus des pages publiques</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Images et textes principaux du site, administrés ici — la Médiathèque reste la source centrale des images.
        Chaque enregistrement est répercuté immédiatement sur le site public.
      </p>

      <h2 className="mt-8 font-heading text-xl text-deepgreen">Images des pages</h2>
      {pages.length === 0 && <div className="mt-4"><EmptyState icon={Images} title="Aucun emplacement">Les emplacements d'images apparaîtront ici.</EmptyState></div>}
      <div className="mt-4 space-y-8">
        {pages.map((group) => (
          <div key={group.page} data-testid={`page-group-${group.page}`}>
            <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">{group.page.replace(/-/g, " ")}</h3>
            <div className="mt-3 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {group.slots.map((s) => (
                <div key={s.slot} className="card-ae p-4" data-testid={`slot-card-${s.slot}`}>
                  <p className="text-sm font-semibold text-deepgreen">{s.label}</p>
                  {s.media ? (
                    <div className="mt-3">
                      <img src={s.media.url} alt={s.media.name} className="aspect-[16/9] w-full rounded-lg object-cover" data-testid={`slot-image-${s.slot}`} />
                      <p className="mt-1.5 truncate text-xs text-muted-foreground">{s.media.name}</p>
                      <div className="mt-2 flex gap-2">
                        <input
                          className="input-ae !py-1.5 !text-xs"
                          placeholder="Texte alternatif (accessibilité)"
                          value={alts[s.media.id] ?? ""}
                          onChange={(e) => setAlts((prev) => ({ ...prev, [s.media.id]: e.target.value }))}
                          data-testid={`slot-alt-${s.slot}`}
                        />
                        <button type="button" onClick={() => saveAlt(s.media.id)} className="btn-secondary-ae !px-3 !py-1.5 !text-[10px] shrink-0" data-testid={`slot-alt-save-${s.slot}`}>
                          <Save className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3 flex aspect-[16/9] items-center justify-center rounded-lg border-2 border-dashed border-sage/50 text-xs text-muted-foreground" data-testid={`slot-empty-${s.slot}`}>
                      Aucune image — visuel par défaut affiché
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button type="button" onClick={() => setPickerSlot(s.slot)} className="btn-primary-ae !px-3.5 !py-2 !text-[10px]" data-testid={`slot-choose-${s.slot}`}>
                      <Images className="h-3.5 w-3.5" /> {s.media ? "Remplacer" : "Choisir"} — Médiathèque
                    </button>
                    <button type="button" onClick={() => fileInputs.current[s.slot]?.click()} disabled={busy === s.slot} className="btn-secondary-ae !px-3.5 !py-2 !text-[10px] disabled:opacity-50" data-testid={`slot-upload-${s.slot}`}>
                      <ImagePlus className="h-3.5 w-3.5" /> {busy === s.slot ? "Import…" : "Importer"}
                    </button>
                    {s.media && (
                      <button type="button" onClick={() => unbind(s.slot)} className="inline-flex items-center gap-1.5 rounded-full border border-red-200 px-3.5 py-2 text-[10px] font-bold uppercase tracking-wider text-red-600 transition-colors hover:bg-red-50" data-testid={`slot-unbind-${s.slot}`}>
                        <Trash2 className="h-3.5 w-3.5" /> Retirer
                      </button>
                    )}
                    <input
                      ref={(el) => { fileInputs.current[s.slot] = el; }}
                      type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                      onChange={(e) => { upload(s.slot, e.target.files?.[0]); e.target.value = ""; }}
                      data-testid={`slot-file-${s.slot}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <h2 className="mt-10 font-heading text-xl text-deepgreen">Textes principaux</h2>
      <div className="mt-4 space-y-4">
        {texts.map((t) => (
          <div key={t.key} className="card-ae p-4" data-testid={`text-card-${t.key}`}>
            <label className="label-ae" htmlFor={`text-${t.key}`}>{t.label}</label>
            <textarea
              id={`text-${t.key}`}
              className="input-ae min-h-20"
              value={drafts[t.key] ?? ""}
              maxLength={2000}
              onChange={(e) => setDrafts((prev) => ({ ...prev, [t.key]: e.target.value }))}
              data-testid={`text-input-${t.key}`}
            />
            <div className="mt-2 flex items-center justify-between">
              <p className="text-[11px] text-muted-foreground">Vide = libellé d'origine du site affiché.</p>
              <button type="button" onClick={() => saveText(t.key)} className="btn-primary-ae !px-4 !py-2 !text-[10px]" data-testid={`text-save-${t.key}`}>
                <Save className="h-3.5 w-3.5" /> Enregistrer
              </button>
            </div>
          </div>
        ))}
      </div>

      <MediaPickerDialog
        open={pickerSlot !== null}
        onOpenChange={(open) => !open && setPickerSlot(null)}
        multiple
        onSelect={(imgs) => pickerSlot && bind(pickerSlot, imgs.at(-1).id)}
        title="Choisir l'image de l'emplacement"
      />
      <div className="mt-8 flex items-center gap-2 rounded-lg bg-sage-light p-4 text-xs text-muted-foreground" data-testid="pages-note">
        <RefreshCw className="h-4 w-4 shrink-0" /> Les modifications sont appliquées dès l'enregistrement — rechargez simplement la page publique concernée.
      </div>
    </div>
  );
}
